// M7 participation UI — mirrors frontend/src/components/Participation.tsx.
// Dual storage: client-generated pseudo_id + status in localStorage,
// signal POSTed to the API. Optional name only with contextual consent
// (P2.6) incl. min-age-16 hint. Re-clicking the active button withdraws.

import type { FunctionComponent } from "preact";
import { useState } from "preact/hooks";

import { postParticipation } from "./api.js";
import { useT } from "./messages.js";
import type { ParticipationCounts, PublicActionDetail } from "./types.js";

const PSEUDO_ID_KEY = "cb_pseudo_id";

function getPseudoId(): string {
  let id = localStorage.getItem(PSEUDO_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(PSEUDO_ID_KEY, id);
  }
  return id;
}

type ActiveStatus = "yes" | "maybe";

interface LocalParticipation {
  status: ActiveStatus;
  name: string;
  consent: boolean;
}

const localKey = (actionId: string) => `cb_participation_${actionId}`;

function getLocal(actionId: string): LocalParticipation | null {
  try {
    const raw = localStorage.getItem(localKey(actionId));
    return raw ? (JSON.parse(raw) as LocalParticipation) : null;
  } catch {
    return null;
  }
}

function setLocalStored(actionId: string, value: LocalParticipation | null) {
  if (value === null) localStorage.removeItem(localKey(actionId));
  else localStorage.setItem(localKey(actionId), JSON.stringify(value));
}

export const ParticipationBox: FunctionComponent<{
  data: PublicActionDetail;
  apiBase: string;
}> = ({ data, apiBase }) => {
  const t = useT();
  const actionId = data.id;
  const [local, setLocal] = useState<LocalParticipation | null>(() => getLocal(actionId));
  const [counts, setCounts] = useState<ParticipationCounts>(data.participation);
  const [name, setName] = useState(local?.name ?? "");
  const [consent, setConsent] = useState(local?.consent ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const myStatus: ActiveStatus | null = local?.status ?? null;
  const nameSaved = Boolean(local?.name && local?.consent);

  async function submit(status: ActiveStatus | "none", sendName: string, sendConsent: boolean) {
    setBusy(true);
    setError(false);
    try {
      const result = await postParticipation(apiBase, actionId, {
        pseudo_id: getPseudoId(),
        status,
        display_name: sendConsent ? sendName.trim() : "",
        name_consent: sendConsent,
      });
      setCounts(result.participation);
      if (status === "none") {
        setLocal(null);
        setLocalStored(actionId, null);
      } else {
        const next = {
          status,
          name: sendConsent ? sendName.trim() : "",
          consent: sendConsent,
        };
        setLocal(next);
        setLocalStored(actionId, next);
      }
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  const onStatusClick = (status: ActiveStatus) => {
    if (myStatus === status) {
      void submit("none", "", false);
    } else {
      void submit(status, nameSaved ? name : "", nameSaved ? consent : false);
    }
  };

  const canSaveName = name.trim().length > 0 && consent && myStatus !== null;

  return (
    <div class="cb-part">
      <p class="cb-part-label">{t("partLabel")}</p>
      <div class="cb-part-buttons">
        <button
          type="button"
          class={`cb-lb-btn${myStatus === "yes" ? " cb-part-active" : ""}`}
          disabled={busy}
          onClick={() => onStatusClick("yes")}
          aria-pressed={myStatus === "yes"}
        >
          {myStatus === "yes" ? "✓ " : ""}{t("partYes")}
          {counts.yes > 0 && <span class="cb-part-count">{counts.yes}</span>}
        </button>
        <button
          type="button"
          class={`cb-lb-btn${myStatus === "maybe" ? " cb-part-active" : ""}`}
          disabled={busy}
          onClick={() => onStatusClick("maybe")}
          aria-pressed={myStatus === "maybe"}
        >
          {myStatus === "maybe" ? "✓ " : ""}{t("partMaybe")}
          {counts.maybe > 0 && <span class="cb-part-count">{counts.maybe}</span>}
        </button>
      </div>
      {myStatus !== null && (
        <p class="cb-part-hint">{t("partWithdraw")}</p>
      )}

      {myStatus !== null && (
        <div class="cb-part-name">
          {nameSaved ? (
            <div class="cb-part-name-saved">
              <p>{t("partNameSaved", { name: local?.name ?? "" })}</p>
              <button
                type="button"
                class="cb-lb-btn cb-lb-btn-sm"
                disabled={busy}
                onClick={() => {
                  setName("");
                  setConsent(false);
                  void submit(myStatus, "", false);
                }}
              >
                {t("partNameRemove")}
              </button>
            </div>
          ) : (
            <>
              <label class="cb-part-name-label" htmlFor="cb-part-name-input">
                {t("partNameOptional")}
              </label>
              <input
                id="cb-part-name-input"
                type="text"
                maxLength={80}
                value={name}
                onInput={(e) => setName((e.target as HTMLInputElement).value)}
                placeholder={t("partNamePlaceholder")}
                class="cb-part-input"
              />
              <label class="cb-part-consent">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent((e.target as HTMLInputElement).checked)}
                />
                <span>{t("partConsent")}</span>
              </label>
              {canSaveName && (
                <button
                  type="button"
                  class="cb-lb-btn cb-part-submit"
                  disabled={busy}
                  onClick={() => void submit(myStatus, name, consent)}
                >
                  {t("partNameSubmit")}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {error && (
        <p class="cb-part-error">{t("partError")}</p>
      )}
    </div>
  );
};
